import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { blogArticles } from "@/mocks/home";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://superbuy.beer";

const articleHeroImages: Record<string, string> = {
  "best-superbuy-spreadsheet-2026": "https://readdy.ai/api/search-image?query=Modern%20clean%20workspace%20with%20large%20monitor%20displaying%20colorful%20spreadsheet%20data%20and%20product%20images%20white%20minimalist%20desk%20natural%20daylight%20bright%20airy%20office%20professional%20atmosphere%20soft%20shadows%20editorial%20photography%20warm%20neutral%20background%20high%20detail&width=1800&height=800&seq=articlehero-v2-01-main&orientation=landscape",
  "how-to-use-superbuy-spreadsheet": "https://readdy.ai/api/search-image?query=Hands%20typing%20on%20laptop%20with%20spreadsheet%20interface%20open%20on%20screen%20clean%20white%20desk%20with%20notebook%20and%20coffee%20bright%20natural%20workspace%20minimalist%20modern%20editorial%20photography%20warm%20neutral%20tones&width=1800&height=800&seq=articlehero-v2-02-tutorial&orientation=landscape",
  "is-superbuy-safe": "https://readdy.ai/api/search-image?query=Security%20shield%20concept%20with%20product%20packages%20and%20checkmark%20on%20clean%20white%20desk%20bright%20studio%20lighting%20minimalist%20editorial%20photography%20trust%20and%20safety%20theme%20professional%20aesthetic&width=1800&height=800&seq=articlehero-v2-03-safety&orientation=landscape",
  "superbuy-shipping-guide": "https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20and%20packages%20with%20labels%20arranged%20on%20clean%20white%20surface%20world%20map%20concept%20in%20background%20bright%20studio%20lighting%20minimalist%20editorial%20product%20photography&width=1800&height=800&seq=articlehero-v2-04-shipping&orientation=landscape",
  "superbuy-vs-pandabuy": "https://readdy.ai/api/search-image?query=Two%20shopping%20carts%20facing%20each%20other%20on%20clean%20modern%20desk%20comparison%20concept%20bright%20natural%20light%20minimalist%20editorial%20photography%20warm%20neutral%20background%20professional&width=1800&height=800&seq=articlehero-v2-05-vs&orientation=landscape",
  "best-sneaker-sellers-superbuy": "https://readdy.ai/api/search-image?query=Collection%20of%20stylish%20designer%20sneakers%20artistically%20displayed%20on%20clean%20white%20surface%20bright%20studio%20lighting%20minimalist%20editorial%20product%20photography%20professional%20aesthetic%20high%20detail%20luxury%20feel&width=1800&height=800&seq=articlehero-v2-06-sneakers&orientation=landscape",
  "superbuy-qc-guide": "https://readdy.ai/api/search-image?query=Magnifying%20glass%20inspecting%20clothing%20fabric%20detail%20with%20measuring%20tape%20on%20clean%20white%20desk%20bright%20studio%20lighting%20minimalist%20editorial%20photography%20quality%20control%20inspection%20theme&width=1800&height=800&seq=articlehero-v2-07-qc&orientation=landscape",
  "superbuy-haul-tips": "https://readdy.ai/api/search-image?query=Organized%20shopping%20haul%20with%20various%20fashion%20clothing%20items%20laid%20out%20beautifully%20on%20clean%20white%20surface%20bright%20natural%20daylight%20minimalist%20editorial%20product%20photography%20warm%20neutral%20tones&width=1800&height=800&seq=articlehero-v2-08-haul&orientation=landscape",
  "best-superbuy-alternatives": "https://readdy.ai/api/search-image?query=Multiple%20shopping%20bags%20from%20different%20stores%20with%20world%20map%20concept%20on%20clean%20modern%20desk%20bright%20natural%20lighting%20minimalist%20editorial%20photography%20warm%20neutral%20background%20professional&width=1800&height=800&seq=articlehero-v2-09-alternatives&orientation=landscape",
  "superbuy-spreadsheet-reddit": "https://readdy.ai/api/search-image?query=Laptop%20screen%20showing%20online%20community%20discussion%20forum%20with%20shopping%20tips%20on%20clean%20bright%20workspace%20minimalist%20modern%20editorial%20photography%20natural%20lighting%20warm%20atmosphere&width=1800&height=800&seq=articlehero-v2-10-reddit&orientation=landscape",
  "how-to-reduce-shipping-costs": "https://readdy.ai/api/search-image?query=Stack%20of%20coins%20next%20to%20shipping%20label%20and%20calculator%20on%20clean%20white%20desk%20bright%20natural%20lighting%20minimalist%20editorial%20photography%20money%20saving%20concept%20warm%20neutral%20background&width=1800&height=800&seq=articlehero-v2-11-save&orientation=landscape",
  "superbuy-buying-guide-beginners": "https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20notes%20next%20to%20laptop%20on%20clean%20white%20desk%20bright%20warm%20natural%20light%20minimalist%20editorial%20photography%20beginner%20friendly%20inviting%20atmosphere%20learning%20concept&width=1800&height=800&seq=articlehero-v2-12-beginner&orientation=landscape",
};

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100">
              <i className="ri-file-unknow-line text-2xl text-accent-600"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroImage = articleHeroImages[article.slug] || articleHeroImages["best-superbuy-spreadsheet-2026"];

  const articleKeywords = article.keywords?.join(", ") || "Superbuy Spreadsheet";
  const canonicalUrl = `${siteUrl}/blog/${article.slug}`;

  const blogSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          "@type": "Organization",
          name: "superbuy.beer",
          url: siteUrl,
        },
        publisher: {
          "@type": "Organization",
          name: "superbuy.beer",
          url: siteUrl,
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
        image: heroImage,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: article.title,
            item: canonicalUrl,
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How do I get started with this resource?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "The best way to begin is by accessing the main Superbuy Spreadsheet through the button at the top of this page. It provides instant access to verified links and community resources organized by category.",
            },
          },
          {
            "@type": "Question",
            name: "Is the information regularly updated?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, the most reliable resources are updated weekly or bi-weekly. Active community members contribute by flagging outdated information and adding new verified sellers and products.",
            },
          },
          {
            "@type": "Question",
            name: "Can I trust all the information provided?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "While community verification significantly reduces risk, it is always wise to perform your own due diligence. Check recent reviews, verify current pricing, and start with smaller orders when trying something new.",
            },
          },
          {
            "@type": "Question",
            name: "How can I contribute to the community?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Community contributions are always welcome. Share your experiences, report outdated information, and recommend new verified sellers to help keep the resources accurate and useful for everyone.",
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <title>{article.title}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={articleKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={heroImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={heroImage} />
      <JsonLd data={blogSchema} />
      <Navbar />
      <main>
        <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>

          <div className="relative z-10 w-full px-4 md:px-6 flex flex-col items-center text-center pt-20">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 backdrop-blur-md border border-background-50/20 mb-4">
              <span className="text-xs font-medium text-background-50">{article.category}</span>
              <span className="text-background-50/60 text-xs">·</span>
              <span className="text-xs text-background-50/80">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-background-50 leading-tight max-w-3xl">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-50/80 max-w-xl">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50/15 backdrop-blur-md border border-background-50/25 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/25 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back to Home
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Complete Guide to {article.title.split(":")[0]}
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  If you are looking for reliable and up-to-date information about{" "}
                  <strong>{article.keywords?.[0] || "Superbuy Spreadsheet"}</strong>, you have
                  come to the right place. This comprehensive guide covers everything you need to
                  know — from getting started to advanced strategies used by experienced
                  international shoppers. Whether you are a complete beginner or a seasoned buyer,
                  this article provides actionable insights that will improve your shopping experience.
                </p>
                <p>
                  The world of international shopping has evolved significantly in 2026. With
                  more sellers, platforms, and tools available than ever before, having access to
                  organized, verified resources like a{" "}
                  <strong>{article.keywords?.[1] || article.keywords?.[0] || "Superbuy Spreadsheet"}</strong>{" "}
                  can make the difference between a successful purchase and a disappointing
                  experience. We have compiled the most important information, practical tips, and
                  expert recommendations to help you navigate this landscape with confidence.
                </p>
                <p>
                  This guide is based on real community experiences, verified data, and extensive
                  research into what works best for international buyers in 2026. Our goal is to
                  provide you with a trustworthy resource that you can refer back to whenever you
                  need guidance on{" "}
                  <strong>{article.keywords?.[2] || article.keywords?.[0] || "international shopping"}</strong>.
                </p>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Key Benefits and Why It Matters
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                Understanding the core advantages helps you make informed decisions. Here are the most
                impactful benefits that experienced buyers consistently report:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: "Time Efficiency", desc: "A well-organized resource saves you hours of manual searching across multiple platforms and seller stores." },
                  { title: "Risk Reduction", desc: "Verified information and community feedback significantly lower the chance of encountering unreliable sellers." },
                  { title: "Cost Optimization", desc: "Compare options easily and identify the best value propositions without compromising on quality." },
                  { title: "Quality Assurance", desc: "Access detailed guidance on what to look for, including QC criteria and quality indicators." },
                  { title: "Continuous Updates", desc: "The best resources are actively maintained with fresh information, removing outdated or dead links." },
                  { title: "Community Knowledge", desc: "Tap into collective experience from thousands of buyers who have already tested and verified the information." },
                ].map((benefit) => (
                  <div key={benefit.title} className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">{benefit.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                How It Works: A Practical Overview
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  Getting started with{" "}
                  <strong>{article.keywords?.[0] || "Superbuy Spreadsheet"}</strong> is
                  straightforward once you understand the basic workflow. The system is designed to
                  be intuitive, but knowing the key steps beforehand makes the process even smoother.
                </p>
                <p>
                  First, you will want to identify exactly what you are looking for. Whether it is a
                  specific product category, a particular seller, or general guidance on a topic, having
                  a clear objective helps you navigate the available resources efficiently. The
                  organized structure of a good resource means you can jump directly to the relevant
                  section without wasting time.
                </p>
                <p>
                  Next, verify the information you find. Even trusted resources should be cross-checked
                  with recent community feedback, as conditions can change quickly in the international
                  shopping landscape. Look for recent reviews, updated ratings, and fresh user
                  experiences that confirm the data is still accurate.
                </p>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Comparison Overview
              </h2>
              <div className="bg-background-100 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-foreground-100/40">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-950">Aspect</th>
                        <th className="text-left py-3 px-4 font-semibold text-accent-700">Best Practice</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-500">Common Approach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { aspect: "Research", best: "Verified community sources", common: "Random search results" },
                        { aspect: "Verification", best: "Multi-source confirmation", common: "Single source trust" },
                        { aspect: "Updates", best: "Weekly refreshed data", common: "Static outdated info" },
                        { aspect: "Community", best: "Active feedback loops", common: "No peer review" },
                        { aspect: "Organization", best: "Categorized and searchable", common: "Scattered information" },
                        { aspect: "Reliability", best: "Track record of accuracy", common: "Unverified claims" },
                      ].map((row, i) => (
                        <tr key={row.aspect} className={`border-b border-foreground-100/20 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}>
                          <td className="py-3 px-4 font-medium text-foreground-800">{row.aspect}</td>
                          <td className="py-3 px-4 text-accent-700 font-medium">{row.best}</td>
                          <td className="py-3 px-4 text-foreground-500">{row.common}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Common Mistakes to Avoid
              </h2>
              <div className="space-y-3">
                {[
                  { title: "Relying on Outdated Information", desc: "Always check when a resource was last updated. Information that was accurate six months ago may no longer be reliable in the fast-moving international shopping space." },
                  { title: "Skipping Verification Steps", desc: "Even trusted sources should be verified. Take a few extra minutes to cross-reference information and check recent community feedback." },
                  { title: "Overlooking Hidden Costs", desc: "When comparing options, factor in all associated costs including shipping, fees, and potential additional charges that may not be immediately obvious." },
                  { title: "Not Reading Recent Reviews", desc: "Overall ratings can be misleading if a seller or service has recently declined in quality. Always read the most recent feedback first." },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-error-warning-line text-xs"></i>
                      </span>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed ml-7">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Safety and Best Practices
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  Safety should always be a top priority when engaging in international shopping.
                  The community has developed several best practices that significantly reduce risk:
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Always verify seller credentials and read recent feedback before placing any order.</li>
                  <li>Use available protection services and never skip verification steps offered by the platform.</li>
                  <li>Start with smaller test orders when trying a new seller or service for the first time.</li>
                  <li>Keep records of all communications, order details, and payment confirmations.</li>
                  <li>Stay informed about common scam patterns and red flags shared by the community.</li>
                </ul>
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 mt-5">
                  <p className="text-xs md:text-sm text-accent-800 leading-relaxed">
                    <strong>Pro Tip:</strong> Experienced buyers recommend maintaining personal notes
                    alongside any public resources you use. Track your own experiences with specific
                    sellers, shipping methods, and product categories. This personal database becomes
                    incredibly valuable for future purchases and helps you spot patterns that generic
                    resources might miss.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {[
                  { q: "How do I get started with this resource?", a: "The best way to begin is by accessing the main Superbuy Spreadsheet through the button at the top of this page. It provides instant access to verified links and community resources organized by category." },
                  { q: "Is the information regularly updated?", a: "Yes, the most reliable resources are updated weekly or bi-weekly. Active community members contribute by flagging outdated information and adding new verified sellers and products." },
                  { q: "Can I trust all the information provided?", a: "While community verification significantly reduces risk, it is always wise to perform your own due diligence. Check recent reviews, verify current pricing, and start with smaller orders when trying something new." },
                  { q: "How can I contribute to the community?", a: "Community contributions are always welcome. Share your experiences, report outdated information, and recommend new verified sellers to help keep the resources accurate and useful for everyone." },
                ].map((faq, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">{faq.q}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Ready to Start Shopping Smarter?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 mb-6 max-w-lg mx-auto">
                Access the best Superbuy Spreadsheet resources, verified seller lists, and complete buying
                guides. Start your next successful purchase today with trusted community resources.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Get Superbuy Spreadsheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-background-50 border border-foreground-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  Back to Home
                </Link>
              </div>
            </section>

            <div className="mt-10 pt-8 border-t border-foreground-100/30">
              <p className="text-xs text-foreground-400 mb-3">Continue Reading:</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">
                  ← Back to Homepage
                </Link>
                {blogArticles.filter((a) => a.slug !== article.slug).slice(0, 3).map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    {related.title.length > 45 ? related.title.substring(0, 45) + "..." : related.title} →
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}